import type { ToolbarAppsConfig, ToolbarNotification, ToolbarNotificationKind } from './notificationTypes';

/** Same output shape as toolbar helper `formatIsoDate` */
export function formatNotificationBroadcastDate(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(2);
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${hour}:${minute} - ${day}/${month}/${year}`;
}

/** Human-friendly line like "Terça-feira · 07:30" for light notification rows */
export function formatNotificationDisplayDate(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${cap} · ${time}`;
}

/**
 * App icon URL when available; otherwise `null` so the UI shows the default check (global / no image).
 */
export function resolveNotificationImage(el: ToolbarNotification, apps: ToolbarAppsConfig | undefined, _resolveAsset: (path: string) => string): string | null {
    if (el.icon) return el.icon;
    console.log('apps', apps);
    const appImage = apps?.[el.empreender_plus_application_code ?? '']?.img;
    const kind = el.type as ToolbarNotificationKind | undefined;

    if (kind === 'application' || kind === 'direct') {
        return typeof appImage === 'string' && appImage.trim() !== '' ? appImage : null;
    }

    return null;
}

export function resolveNotificationName(el: ToolbarNotification, apps: ToolbarAppsConfig | undefined, translate: (key: string) => string): string {
    const appName = apps?.[el.empreender_plus_application_code ?? '']?.faq?.name;

    const notificationName: Record<ToolbarNotificationKind, string | undefined> = {
        global: translate('Geral'),
        application: appName,
        direct: appName,
    };

    const kind = el.type as ToolbarNotificationKind | undefined;
    return (kind && notificationName[kind]) ?? translate('Geral');
}

function hasTimePassedOneHourAndAtLeastOneDay(notification: ToolbarNotification): ToolbarNotification | null {
    const availableTime = new Date(notification?.available_at ?? '');
    if (Number.isNaN(availableTime.getTime())) return null;

    const currentTime = new Date();
    const hourPassed = (currentTime.getTime() - availableTime.getTime()) / (1000 * 60 * 60) >= 1;

    const startOfCurrentDay = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
    const startOfAvailableDay = new Date(availableTime.getFullYear(), availableTime.getMonth(), availableTime.getDate());
    const dayDifference = (startOfCurrentDay.getTime() - startOfAvailableDay.getTime()) / (1000 * 60 * 60 * 24);
    const dayPassed = dayDifference >= 1;

    return hourPassed || dayPassed ? notification : null;
}

export function removeRecurrentNotificationsThatAlreadyPassed(notificationsToUse: ToolbarNotification[] | undefined): ToolbarNotification[] {
    if (!notificationsToUse?.length) return [];

    const recurrentNotifications = notificationsToUse.filter((notification) => {
        if (notification.recurring_notification_id && notification.cron) {
            return hasTimePassedOneHourAndAtLeastOneDay(notification);
        }
        return false;
    });

    return notificationsToUse.filter((mainItem) => !recurrentNotifications.some((secondaryItem) => secondaryItem.id === mainItem.id));
}

export function dedupeNotificationsById(items: ToolbarNotification[]): ToolbarNotification[] {
    const seen = new Set<string | number>();
    const out: ToolbarNotification[] = [];
    for (const n of items) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push(n);
    }
    return out;
}

export function shouldShowNotificationForFilter(notification: ToolbarNotification, filterApplicationCode: string): boolean {
    if (filterApplicationCode === '') return true;
    return notification.empreender_plus_application_code === filterApplicationCode;
}
